/* UserAvatar — small circular avatar showing the logged-in user's Google
 * profile picture (or initials fallback). Sits in the dashboard header.
 * Click to see full name + email tooltip. */
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

function initialsOf(name = '', email = '') {
  const src = (name || email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (src[0] || '?').toUpperCase();
}

export default function UserAvatar() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!user || user === false) return null;

  const name = user.name || '';
  const email = user.email || '';
  const picture = user.picture && imgOk ? user.picture : null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-9 h-9 rounded-full overflow-hidden border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] transition"
        title={name || email}
        data-testid="user-avatar-btn"
        aria-label={`Profil: ${name || email}`}
      >
        {picture ? (
          <img
            src={picture}
            alt={name || email}
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FFC8DD] via-[#C8B6FF] to-[#A2D2FF] flex items-center justify-center font-bold text-sm">
            {initialsOf(name, email)}
          </div>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-[200] w-64 bg-white rounded-2xl border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-3"
          data-testid="user-avatar-popover"
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-black flex-shrink-0">
              {picture ? (
                <img src={picture} alt={name || email} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#FFC8DD] via-[#C8B6FF] to-[#A2D2FF] flex items-center justify-center font-bold">
                  {initialsOf(name, email)}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold font-['Fredoka'] text-sm truncate" data-testid="user-avatar-name">
                {name || 'Ingen namn ännu'}
              </div>
              <div className="text-[11px] text-gray-500 truncate" data-testid="user-avatar-email">
                {email}
              </div>
              {user.auth_provider === 'google' && (
                <div className="text-[10px] text-purple-600 font-bold mt-0.5 flex items-center gap-1">
                  <svg width="10" height="10" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.66 4.1-5.5 4.1-3.31 0-6-2.73-6-6.1s2.69-6.1 6-6.1c1.88 0 3.14.79 3.87 1.48l2.63-2.53C16.85 3.32 14.63 2.4 12 2.4 6.75 2.4 2.5 6.65 2.5 12s4.25 9.6 9.5 9.6c5.48 0 9.11-3.85 9.11-9.28 0-.62-.07-1.1-.16-1.57H12z"/>
                  </svg>
                  Inloggad via Google
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
